import React from "react";
import { Link } from "@inertiajs/inertia-react";
import {
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
  StarIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import CustomerLayout from "../../../Layouts/CustomerLayout";
import ImageGallery from "../../../Components/Domestic/ImageGallery";
import StarRating from "../../../Components/Domestic/StarRating";

const PLATFORM_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X / Twitter",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  other: "Website",
};

const TradesmanProfile = ({ request_id, tradesman }) => (
  <CustomerLayout title="Tradesperson profile">
    <div className="max-w-2xl space-y-6">
      <Link
        href={`/domestic/requests/${request_id}`}
        className="text-sm font-medium text-orange-700 hover:text-orange-600"
      >
        &larr; Back to request
      </Link>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-7">
        <div className="flex items-center gap-4">
          {tradesman.avatar_url ? (
            <img
              src={tradesman.avatar_url}
              alt=""
              className="h-16 w-16 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <UserCircleIcon className="h-16 w-16 flex-shrink-0 text-gray-300" />
          )}
          <div>
          <h2 className="flex flex-wrap items-center gap-2 text-xl font-semibold text-gray-900">
            <span>{tradesman.name}</span>
            {tradesman.star_rating ? (
              <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-600">
                <StarIcon className="h-4 w-4 text-yellow-400" />
                {Number(tradesman.star_rating).toFixed(1)}
              </span>
            ) : null}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {tradesman.trades.join(", ")}
          </p>
          </div>
        </div>

        {tradesman.about && (
          <p className="mt-5 whitespace-pre-wrap border-t border-gray-100 pt-5 text-sm text-gray-700">
            {tradesman.about}
          </p>
        )}

        <dl className="mt-5 space-y-2 border-t border-gray-100 pt-5 text-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <a href={`mailto:${tradesman.email}`} className="hover:text-orange-600">
              {tradesman.email}
            </a>
          </div>
          {tradesman.phone && (
            <div className="flex items-center gap-2 text-gray-700">
              <PhoneIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <a href={`tel:${tradesman.phone}`} className="hover:text-orange-600">
                {tradesman.phone}
              </a>
            </div>
          )}
          {tradesman.website_url && (
            <div className="flex items-center gap-2 text-gray-700">
              <GlobeAltIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <a
                href={tradesman.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-orange-600"
              >
                {tradesman.website_url}
              </a>
            </div>
          )}
        </dl>

        {tradesman.social_links.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
            {tradesman.social_links.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:border-orange-300 hover:text-orange-600"
              >
                {PLATFORM_LABELS[link.platform] || link.platform}
              </a>
            ))}
          </div>
        )}

        {tradesman.images.length > 0 && (
          <div className="mt-5 border-t border-gray-100 pt-5">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">
              Photos of past work
            </h3>
            <ImageGallery images={tradesman.images} />
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-7">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Reviews</h3>
          {tradesman.average_rating && (
            <div className="flex items-center gap-2">
              <StarRating value={tradesman.average_rating} size="h-4 w-4" />
              <span className="text-sm text-gray-500">
                {tradesman.average_rating.toFixed(1)} ({tradesman.review_count})
              </span>
            </div>
          )}
        </div>
        {tradesman.reviews.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No reviews yet.</p>
        ) : (
          <ul className="mt-4 space-y-4">
            {tradesman.reviews.map((r) => (
              <li key={r.id} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between">
                  <StarRating value={r.rating} size="h-4 w-4" />
                  <span className="text-xs text-gray-400">{r.created_at}</span>
                </div>
                {r.note && (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{r.note}</p>
                )}
                <p className="mt-1 text-xs text-gray-400">Verified customer</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </CustomerLayout>
);

export default TradesmanProfile;
